import {
  CTCSS_TONES,
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BLOCK_SIZE = 32
const CHANNEL_COUNT = 199
const MEMORY_START = 0x0008
const NAME_START = 0x0d48
const USED_START = 0x1a08
const SCAN_START = 0x1a28
const POWER_MASK = 0x0c
const NARROW_MASK = 0x10
const CHIRP_MARKER = Uint8Array.from([
  0x00, 0xff, 0x63, 0x68, 0x69, 0x72, 0x70, 0xee, 0x69, 0x6d, 0x67, 0x00, 0x01,
])

type TDH8Spec = {
  id: string
  vendor: string
  model: string
  summary: string
  memorySizes: readonly number[]
  magics: readonly (readonly number[])[]
  identity: readonly number[]
  powerLevels: readonly string[]
}

export type TDH8Profile = {
  protocol: 'tdh8'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 38400
  channelMaximum: number
  nameLength: 8
  memorySize: number
  memorySizes: readonly number[]
  powerLevels: readonly string[]
  magics: readonly (readonly number[])[]
  identity: readonly number[]
}

function ascii(value: string): readonly number[] {
  return Array.from(value, (character) => character.charCodeAt(0) & 0xff)
}

const MAGIC_H8 = [0x50, 0x56, 0x4f, 0x4a, 0x48, 0x1c, 0x14] as const
const MAGIC_H8_G3 = [0x50, 0x56, 0x4f, 0x4a, 0x48, 0x3c, 0x14] as const
const MAGIC_H3 = [0x50, 0x56, 0x4f, 0x4a, 0x48, 0x5c, 0x14] as const
const MAGIC_RT730 = [0x50, 0x47, 0x4f, 0x4a, 0x48, 0xc3, 0x44] as const

const SPECS: readonly TDH8Spec[] = [
  {
    id: 'tidradio-td-h8',
    vendor: 'TIDRADIO',
    model: 'TD-H8',
    summary: 'TIDRADIO TD-H8 color-screen handheld.',
    memorySizes: [0x1eef, 0x1fef],
    magics: [MAGIC_H8, MAGIC_H8_G3],
    identity: ascii('P31183\u00ff\u00ff'),
    powerLevels: ['Low', 'Mid', 'High'],
  },
  {
    id: 'tidradio-td-h8-ham',
    vendor: 'TIDRADIO',
    model: 'TD-H8-HAM',
    summary: 'TIDRADIO TD-H8 HAM edition color-screen handheld.',
    memorySizes: [0x1eef, 0x1fef],
    magics: [MAGIC_H8, MAGIC_H8_G3],
    identity: ascii('P31185\u00ff\u00ff'),
    powerLevels: ['Low', 'Mid', 'High'],
  },
  {
    id: 'tidradio-td-h8-gmrs',
    vendor: 'TIDRADIO',
    model: 'TD-H8-GMRS',
    summary: 'TIDRADIO TD-H8 GMRS edition color-screen handheld.',
    memorySizes: [0x1eef, 0x1fef],
    magics: [MAGIC_H8, MAGIC_H8_G3],
    identity: ascii('P31184\u00ff\u00ff'),
    powerLevels: ['Low', 'High'],
  },
  {
    id: 'tid-td-uv68',
    vendor: 'TID',
    model: 'TD-UV68',
    summary: 'TID TD-UV68 color-screen handheld.',
    memorySizes: [0x1eef],
    magics: [MAGIC_H8],
    identity: ascii('P31183\u00ff\u00ff'),
    powerLevels: ['Low', 'Mid', 'High'],
  },
  {
    id: 'tidradio-td-h3',
    vendor: 'TIDRADIO',
    model: 'TD-H3',
    summary: 'TIDRADIO TD-H3 color-screen handheld.',
    memorySizes: [0x1fef],
    magics: [MAGIC_H3],
    identity: ascii('P31183\u00ff\u00ff'),
    powerLevels: ['Low', 'High'],
  },
  {
    id: 'tidradio-td-h3-ham',
    vendor: 'TIDRADIO',
    model: 'TD-H3-HAM',
    summary: 'TIDRADIO TD-H3 HAM edition color-screen handheld.',
    memorySizes: [0x1fef],
    magics: [MAGIC_H3],
    identity: ascii('P31185\u00ff\u00ff'),
    powerLevels: ['Low', 'High'],
  },
  {
    id: 'tidradio-td-h3-gmrs',
    vendor: 'TIDRADIO',
    model: 'TD-H3-GMRS',
    summary: 'TIDRADIO TD-H3 GMRS edition color-screen handheld.',
    memorySizes: [0x1fef],
    magics: [MAGIC_H3],
    identity: ascii('P31184\u00ff\u00ff'),
    powerLevels: ['Low', 'High'],
  },
  {
    id: 'radtel-rt-730',
    vendor: 'Radtel',
    model: 'RT-730',
    summary: 'Radtel RT-730 color-screen handheld.',
    memorySizes: [0x6400],
    magics: [MAGIC_RT730],
    identity: ascii('PGOJH\u00c3D'),
    powerLevels: ['Low', 'Mid', 'High'],
  },
]

export const TDH8_PROFILES: readonly TDH8Profile[] = SPECS.map((spec) => ({
  protocol: 'tdh8',
  id: spec.id,
  vendor: spec.vendor,
  model: spec.model,
  summary: spec.summary,
  tags: [
    'Experimental',
    'Read + write',
    'Color screen',
    `${CHANNEL_COUNT} channels`,
  ],
  baudRate: 38400,
  channelMaximum: CHANNEL_COUNT,
  nameLength: 8,
  memorySize: Math.max(...spec.memorySizes),
  memorySizes: spec.memorySizes,
  powerLevels: spec.powerLevels,
  magics: spec.magics,
  identity: spec.identity,
}))

function findMarker(bytes: Uint8Array): number {
  for (let offset = 0; offset <= bytes.length - CHIRP_MARKER.length; offset += 1) {
    if (CHIRP_MARKER.every((value, index) => bytes[offset + index] === value)) {
      return offset
    }
  }
  return -1
}

export function extractTDH8Image(
  profile: TDH8Profile,
  bytes: Uint8Array,
): Uint8Array {
  const payloadLength = findMarker(bytes)
  const available = payloadLength < 0 ? bytes.byteLength : payloadLength
  const memorySize = [...profile.memorySizes]
    .sort((left, right) => right - left)
    .find((size) => available >= size)
  if (!memorySize) {
    throw new Error(
      `This file is ${available.toLocaleString()} bytes; ${profile.model} requires at least ${Math.min(...profile.memorySizes).toLocaleString()}.`,
    )
  }
  return bytes.slice(0, memorySize)
}

function emptyChannel(number: number, powerLevels: readonly string[]): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: powerLevels[0],
    skip: false,
  }
}

function bcdValue(raw: Uint8Array): number | null {
  if (raw.every((value) => value === 0xff)) return null
  let value = 0
  for (let index = raw.length - 1; index >= 0; index -= 1) {
    const high = raw[index] >> 4
    const low = raw[index] & 0x0f
    value = value * 100 + high * 10 + low
  }
  return value
}

function decimalBcd(value: number, size: number): Uint8Array {
  const result = new Uint8Array(size)
  for (let index = 0; index < size; index += 1) {
    const part = value % 100
    result[index] = ((Math.floor(part / 10) << 4) | (part % 10)) & 0xff
    value = Math.floor(value / 100)
  }
  return result
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function decodeTone(raw: Uint8Array): DecodedTone {
  const value = bcdValue(raw)
  if (value === null || value === 0 || value === 16665) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  if (value >= 12000) {
    return { mode: 'dtcs', ctcss: null, dtcs: value - 12000, polarity: 'R' }
  }
  if (value >= 8000) {
    return { mode: 'dtcs', ctcss: null, dtcs: value - 8000, polarity: 'N' }
  }
  return { mode: 'ctcss', ctcss: value / 10, dtcs: null, polarity: 'N' }
}

function encodeTone(channel: WebChannel): Uint8Array {
  if (channel.toneMode === 'none') return Uint8Array.of(0xff, 0xff)
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    if (!CTCSS_TONES.includes(channel.ctcss as (typeof CTCSS_TONES)[number])) {
      throw new Error('That CTCSS tone is not supported by this radio.')
    }
    return decimalBcd(Math.round(channel.ctcss * 10), 2)
  }
  if (
    channel.toneMode === 'dtcs' &&
    channel.dtcs &&
    DTCS_CODES.includes(channel.dtcs as (typeof DTCS_CODES)[number])
  ) {
    const tone = decimalBcd(channel.dtcs, 2)
    tone[1] |= channel.dtcsPolarity === 'R' ? 0xc0 : 0x80
    return tone
  }
  throw new Error('The selected tone is not supported by this radio.')
}

function toneMatches(channel: WebChannel, tone: DecodedTone): boolean {
  if (channel.toneMode === 'none') return tone.mode === 'none'
  if (channel.toneMode === 'ctcss') {
    return tone.mode === 'ctcss' && tone.ctcss === channel.ctcss
  }
  return (
    tone.mode === 'dtcs' &&
    tone.dtcs === channel.dtcs &&
    tone.polarity === channel.dtcsPolarity
  )
}

function decodeName(raw: Uint8Array): string {
  let end = raw.findIndex((value) => value === 0 || value === 0xff)
  if (end < 0) end = raw.length
  return String.fromCharCode(...raw.slice(0, end)).trim()
}

function channelOffset(number: number): number {
  return MEMORY_START + number * 16
}

function nameOffset(number: number): number {
  return NAME_START + (number - 1) * 16
}

function bitSet(image: Uint8Array, start: number, number: number): boolean {
  const index = number - 1
  return (image[start + (index >> 3)] & (1 << (index & 7))) !== 0
}

function setBit(
  image: Uint8Array,
  start: number,
  number: number,
  value: boolean,
): void {
  const index = number - 1
  const offset = start + (index >> 3)
  const bit = 1 << (index & 7)
  image[offset] = value ? image[offset] | bit : image[offset] & ~bit
}

export function parseTDH8Channel(
  image: Uint8Array,
  profile: TDH8Profile,
  number: number,
): WebChannel {
  const offset = channelOffset(number)
  const raw = image.slice(offset, offset + 16)
  if (raw.byteLength !== 16 || raw[0] === 0xff) {
    return emptyChannel(number, profile.powerLevels)
  }
  const rxFrequency = decodeLbcd(raw.slice(0, 4))
  if (!rxFrequency) return emptyChannel(number, profile.powerLevels)
  const txFrequency =
    raw[4] === 0xff ? null : decodeLbcd(raw.slice(4, 8))
  const txTone = decodeTone(raw.slice(10, 12))
  const rxtone = decodeTone(raw.slice(8, 10))
  const selected = txTone.mode === 'none' ? rxtone : txTone
  const powerIndex = (raw[14] & POWER_MASK) >> 2
  return {
    number,
    empty: false,
    name: decodeName(image.slice(nameOffset(number), nameOffset(number) + 8)),
    rxFrequency,
    txFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode: raw[14] & NARROW_MASK ? 'NFM' : 'FM',
    power: profile.powerLevels[powerIndex] ?? profile.powerLevels[0],
    skip: !bitSet(image, SCAN_START, number),
  }
}

export function parseTDH8Channels(
  image: Uint8Array,
  profile: TDH8Profile,
): WebChannel[] {
  return Array.from(
    { length: CHANNEL_COUNT },
    (_, index) => parseTDH8Channel(image, profile, index + 1),
  )
}

export function applyTDH8Channel(
  image: Uint8Array,
  profile: TDH8Profile,
  channel: WebChannel,
): void {
  if (channel.number < 1 || channel.number > CHANNEL_COUNT) {
    throw new Error(`Channel must be between 1 and ${CHANNEL_COUNT}.`)
  }
  setBit(image, USED_START, channel.number, !channel.empty)
  const offset = channelOffset(channel.number)
  const raw = image.subarray(offset, offset + 16)
  if (channel.empty) {
    raw.fill(0xff)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  const wasEmpty = raw[0] === 0xff
  if (wasEmpty) raw.fill(0)
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  if (channel.txFrequency) {
    encodeLbcd(channel.txFrequency, raw.subarray(4, 8))
  } else {
    raw.subarray(4, 8).fill(0xff)
  }
  if (
    !toneMatches(channel, decodeTone(raw.slice(10, 12))) ||
    (!toneMatches(channel, decodeTone(raw.slice(8, 10))) &&
      decodeTone(raw.slice(8, 10)).mode !== 'none')
  ) {
    const tone = encodeTone(channel)
    raw.set(tone, 8)
    raw.set(tone, 10)
  }
  raw[14] =
    (raw[14] & ~(POWER_MASK | NARROW_MASK)) |
    ((Math.max(0, profile.powerLevels.indexOf(channel.power)) & 0x03) << 2) |
    (channel.mode === 'NFM' ? NARROW_MASK : 0)
  const name = channel.name
    .toUpperCase()
    .replace(/[^\x20-\x7e]/g, '')
    .slice(0, 8)
  image.fill(0, nameOffset(channel.number), nameOffset(channel.number) + 8)
  for (let index = 0; index < name.length; index += 1) {
    image[nameOffset(channel.number) + index] = name.charCodeAt(index)
  }
  setBit(image, SCAN_START, channel.number, !channel.skip)
}

function checksum(data: Uint8Array): number {
  let total = 0
  for (const value of data) total = (total + value) & 0xff
  return total
}

function identityMatches(actual: Uint8Array, expected: readonly number[]): boolean {
  return expected.every((value, index) => actual[index] === value)
}

async function readAck(
  transport: RadioTransport,
  message: string,
): Promise<void> {
  if ((await transport.readExact(1, 3000))[0] !== ACK) throw new Error(message)
}

async function enterProgrammingMode(
  profile: TDH8Profile,
  transport: RadioTransport,
): Promise<number> {
  let lastError: unknown = null
  for (const magic of profile.magics) {
    try {
      await transport.discardInput()
      await transport.write(Uint8Array.from(magic))
      await readAck(transport, 'The radio refused TID programming mode.')
      await transport.write(Uint8Array.of(0x02))
      const response: number[] = []
      for (let index = 0; index < 12; index += 1) {
        const byte = (await transport.readExact(1, 2000))[0]
        response.push(byte)
        if (byte === 0xdd || response.length === 8) break
      }
      const bytes = Uint8Array.from(response)
      const ident =
        bytes.length === 12
          ? Uint8Array.of(bytes[0], bytes[3], bytes[5], ...bytes.slice(7))
          : bytes
      if (ident.byteLength !== 8 || !identityMatches(ident, profile.identity)) {
        throw new Error(
          `The connected radio does not identify as ${profile.vendor} ${profile.model}.`,
        )
      }
      await transport.write(Uint8Array.of(ACK))
      await readAck(transport, 'The radio rejected the TID clone session.')
      return profile.memorySizes[profile.magics.indexOf(magic)] ??
        profile.memorySize
    } catch (cause) {
      lastError = cause
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new Error('No supported TID radio responded.')
}

async function exitProgrammingMode(transport: RadioTransport): Promise<void> {
  await transport.write(new TextEncoder().encode('E'))
}

async function readBlock(
  transport: RadioTransport,
  address: number,
): Promise<Uint8Array> {
  await transport.write(
    Uint8Array.of(0x52, (address >> 8) & 0xff, address & 0xff, BLOCK_SIZE),
  )
  const response = await transport.readExact(BLOCK_SIZE + 5, 4000)
  if (
    response[0] !== 0x57 ||
    response[1] !== ((address >> 8) & 0xff) ||
    response[2] !== (address & 0xff) ||
    response[3] !== BLOCK_SIZE
  ) {
    throw new Error(`Unexpected TID read reply at 0x${address.toString(16)}.`)
  }
  return response.slice(4, -1)
}

export async function readTDH8RadioWithTransport(
  profile: TDH8Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  const memorySize = await enterProgrammingMode(profile, transport)
  try {
    const image = new Uint8Array(memorySize).fill(0xff)
    image.set(profile.identity, 0)
    const total = Math.ceil(memorySize / BLOCK_SIZE)
    for (let index = 0; index < total; index += 1) {
      const address = index * BLOCK_SIZE
      const block = await readBlock(transport, address)
      const length = Math.min(BLOCK_SIZE, memorySize - (MEMORY_START + address))
      if (length > 0) image.set(block.slice(0, length), MEMORY_START + address)
      onProgress?.({
        current: index + 1,
        total,
        label: 'Reading TID color-screen memory',
      })
    }
    return { image, reportedModel: profile.model }
  } finally {
    await exitProgrammingMode(transport)
  }
}

export async function writeTDH8RadioWithTransport(
  profile: TDH8Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (!profile.memorySizes.includes(image.byteLength)) {
    throw new Error('The selected image size does not match this TID radio.')
  }
  await enterProgrammingMode(profile, transport)
  try {
    const total = Math.ceil(image.byteLength / BLOCK_SIZE)
    for (let index = 0; index < total; index += 1) {
      const address = index * BLOCK_SIZE
      const data = new Uint8Array(BLOCK_SIZE).fill(0xff)
      const sourceStart = MEMORY_START + address
      const sourceEnd = Math.min(image.byteLength, sourceStart + BLOCK_SIZE)
      if (sourceStart < image.byteLength) {
        data.set(image.slice(sourceStart, sourceEnd))
      }
      const frame = new Uint8Array(5 + BLOCK_SIZE)
      frame.set([
        0x57,
        (address >> 8) & 0xff,
        address & 0xff,
        BLOCK_SIZE,
      ])
      frame.set(data, 4)
      frame[4 + BLOCK_SIZE] = checksum(data)
      await transport.write(frame)
      await readAck(
        transport,
        `The radio rejected TID write block 0x${address.toString(16)}.`,
      )
      onProgress?.({
        current: index + 1,
        total,
        label: 'Writing TID color-screen memory',
      })
    }
  } finally {
    await exitProgrammingMode(transport)
  }
}
